import {
  CreateExtractionUploadRequest,
  ExtractionSummary,
  ExtractionUploadResponse,
  extractionIdFor,
  Language,
  ocrExtractionIdFor,
  PageText,
  pageCharCounts,
  StudyMaterialText,
} from '@quizdb/common';
import {
  EXTRACTOR_VERSION,
  ExtractionProgress,
  ExtractPdf,
  MaterialPrepError,
  MaterialPrepPhase,
  OCR_VERSION,
  OcrProgress,
  PreparedMaterial,
} from './extraction/extractionTypes';
import { uploadExtraction } from './uploadExtraction';

/**
 * What `prepareMaterial` needs to turn a PDF into an upload-ready extraction. The page's
 * composition supplies these: `hash` is `hashFile` (issue 09), `lookup` is the page's
 * `(id) => getExtraction(id).unwrap().catch((e) => problemCode(e) === 'ExtractionNotFound' ?
 * undefined : Promise.reject(e))`, `createUpload` is `(body) => createExtractionUpload(body
 * ).unwrap()`, `extract` is `extractPdf` (issue 13), and `ocrPages` reads the given pages by OCR
 * on an engine it owns (PHO-D10). This module never imports `extractPdf.ts` or the OCR modules,
 * so a test can stub `extract` and `ocrPages` without a worker.
 */
export interface PrepareMaterialDeps {
  hash: (file: Blob) => Promise<string>;
  lookup: (id: string) => Promise<ExtractionSummary | undefined>;
  createUpload: (body: CreateExtractionUploadRequest) => Promise<ExtractionUploadResponse>;
  extract: ExtractPdf;
  ocrPages: (
    file: Blob,
    pages: number[],
    language: Language,
    onProgress: (progress: OcrProgress) => void,
    signal: AbortSignal
  ) => Promise<PageText[]>;
  /** Overrides the PUT's `fetch`, the way `prepareMaterial_test` stubs it for S3. */
  put?: typeof fetch;
}

/** `text` with each of `ocrText`'s pages replacing that page's text, every other page kept. */
const mergeOcrText = (text: StudyMaterialText, ocrText: readonly PageText[]): StudyMaterialText => {
  const byPage = new Map(text.pages.map((page) => [page.page, page.text]));
  for (const page of ocrText) {
    byPage.set(page.page, page.text);
  }
  return {
    ...text,
    pages: [...byPage]
      .sort(([a], [b]) => a - b)
      .map(([page, pageText]) => ({ page, text: pageText })),
  };
};

/**
 * Turns `file` into an extraction the API can build a job from: hashes it, checks whether that
 * hash (at the current `EXTRACTOR_VERSION`) is already extracted (QZ-D83), and if not, extracts
 * it locally (PTX-D3, PTX-D4). Pages whose text layer is almost empty (PHO-D12) are read by OCR
 * in `pageLanguage` (QZ-D92, PHO-D1), and the result is cached under the OCR id (PHO-D6). The
 * extraction is then uploaded. `onPhase` reports progress for the new-job page's step indicator,
 * an `OcrProgress` only in `'reading'`; `signal` lets picking another file cancel the work in
 * progress (PTX-D9).
 */
export const prepareMaterial = async (
  file: File,
  deps: PrepareMaterialDeps,
  onPhase: (phase: MaterialPrepPhase, progress?: ExtractionProgress | OcrProgress) => void,
  signal: AbortSignal,
  pageLanguage: Language
): Promise<PreparedMaterial> => {
  const checkAborted = (): void => {
    if (signal.aborted) {
      throw new DOMException('Aborted', 'AbortError');
    }
  };
  const uploadDeps = { createUpload: deps.createUpload, put: deps.put };

  try {
    checkAborted();
    onPhase('hashing');
    const sha256 = await deps.hash(file);
    const plainId = extractionIdFor(sha256, EXTRACTOR_VERSION);

    checkAborted();
    onPhase('checking');
    const existing = await deps.lookup(plainId);
    // A pick replaced while awaiting must never resolve, not even on a cache hit.
    checkAborted();
    if (existing) {
      // A plain id is only ever stored for a PDF with no OCR pages (PHO-D6).
      return {
        extractionId: plainId,
        name: file.name,
        pdfPageCount: existing.pdfPageCount,
        pageChars: existing.pageChars,
      };
    }

    onPhase('extracting');
    const { text, ocrPages } = await deps.extract(
      file,
      (progress) => onPhase('extracting', progress),
      signal
    );
    checkAborted();

    if (ocrPages.length === 0) {
      onPhase('uploading');
      await uploadExtraction(plainId, text, uploadDeps, signal);
      checkAborted();
      return {
        extractionId: plainId,
        name: file.name,
        pdfPageCount: text.pdfPageCount,
        pageChars: pageCharCounts(text),
        text,
      };
    }

    const ocrId = ocrExtractionIdFor(sha256, EXTRACTOR_VERSION, OCR_VERSION, pageLanguage);
    onPhase('checking');
    const ocrExisting = await deps.lookup(ocrId);
    checkAborted();
    if (ocrExisting) {
      // No `text`: the local one lacks the OCR pages, so the preview fetches them.
      return {
        extractionId: ocrId,
        name: file.name,
        pdfPageCount: ocrExisting.pdfPageCount,
        pageChars: ocrExisting.pageChars,
        ocrPages,
        pageLanguage,
      };
    }

    onPhase('reading');
    const ocrText = await deps.ocrPages(
      file,
      ocrPages,
      pageLanguage,
      (progress) => onPhase('reading', progress),
      signal
    );
    checkAborted();
    const merged = mergeOcrText(text, ocrText);

    onPhase('uploading');
    await uploadExtraction(ocrId, merged, uploadDeps, signal);
    checkAborted();

    return {
      extractionId: ocrId,
      name: file.name,
      pdfPageCount: merged.pdfPageCount,
      pageChars: pageCharCounts(merged),
      text: merged,
      ocrPages,
      pageLanguage,
    };
  } catch (thrown) {
    if (thrown instanceof MaterialPrepError) {
      throw thrown;
    }
    if (thrown instanceof DOMException && thrown.name === 'AbortError') {
      throw thrown;
    }
    throw new MaterialPrepError('unknown');
  }
};
